import { React, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form } from 'react-bootstrap';
import { CATEGORY_OPTIONS, ALL_CATEGORY_VALUES, districtLabel } from '../../common/siteFilters';
import styles from './AdminStudentFilter.module.css';

function AdminStudentFilter(props) {
  const {
    isOpen,
    setIsOpen,
    states,
    areas,
    sites,
    years,
    grades,
    districts,
    siteMapReady,
    filters,
    setFilters,
    queryPieCharts,
  } = props;
  const [showStates, setShowStates] = useState([]);
  const [showAreas, setShowAreas] = useState([]);
  const [showSites, setShowSites] = useState([]);
  const [showYears, setShowYears] = useState([]);
  const [showGrades, setShowGrades] = useState([]);
  const [showDistricts, setShowDistricts] = useState([]);
  const [showCategories, setShowCategories] = useState(ALL_CATEGORY_VALUES);

  // Default any group with no committed filter to fully checked as its option
  // list arrives (the data loads async). A committed filter is a list of allowed
  // values (legacy) or the selected subset (districts/categories).
  useEffect(() => {
    setShowStates(filters.states || states);
  }, [states]);
  useEffect(() => {
    setShowAreas(filters.areas || areas);
  }, [areas]);
  useEffect(() => {
    setShowSites(filters.sites || sites);
  }, [sites]);
  useEffect(() => {
    setShowYears(filters.years || years);
  }, [years]);
  useEffect(() => {
    setShowGrades(filters.grades || grades);
  }, [grades]);
  useEffect(() => {
    setShowDistricts(filters.districts || districts);
  }, [districts]);

  // Whenever the modal opens, re-sync every draft group from the committed
  // filters so a chip removed on the page below is reflected here and the draft
  // never drifts from what is actually filtering. Legacy groups fall back to the
  // full list; the new groups fall back to fully checked (filter off).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setShowStates(filters.states || states);
    setShowAreas(filters.areas || areas);
    setShowSites(filters.sites || sites);
    setShowYears(filters.years || years);
    setShowGrades(filters.grades || grades);
    setShowDistricts(filters.districts || districts);
    setShowCategories(filters.categories || ALL_CATEGORY_VALUES);
  }, [isOpen]);

  const areAllStatesChecked = () => states.length === showStates.length;
  const areAllAreasChecked = () => areas.length === showAreas.length;
  const areAllSitesChecked = () => sites.length === showSites.length;
  const areAllYearsChecked = () => years.length === showYears.length;
  const areAllGradesChecked = () => grades.length === showGrades.length;
  const areAllDistrictsChecked = () => districts.length === showDistricts.length;
  const areAllCategoriesChecked = () => ALL_CATEGORY_VALUES.length === showCategories.length;

  const toggleValue = (value, list, setList) => {
    if (list.includes(value)) {
      setList(list.filter(item => item !== value));
    } else {
      setList([...list, value]);
    }
  };

  const toggleAll = (allChecked, fullList, setList) => {
    setList(allChecked ? [] : fullList);
  };

  // Reset all filters. This sets each value back to checked.
  const resetFilters = () => {
    setShowStates(states);
    setShowAreas(areas);
    setShowSites(sites);
    setShowYears(years);
    setShowGrades(grades);
    setShowDistricts(districts);
    setShowCategories(ALL_CATEGORY_VALUES);
  };

  const applyFilters = () => {
    // Legacy axes are all-or-nothing: all checked => keep the full list (the
    // backend treats an empty list as "show nothing", so we always send a
    // concrete list). A proper subset keeps only the checked values.
    const statesFilter = areAllStatesChecked() ? states : showStates;
    const areasFilter = areAllAreasChecked() ? areas : showAreas;
    const sitesFilter = areAllSitesChecked() ? sites : showSites;
    const yearsFilter = areAllYearsChecked() ? years : showYears;
    const gradesFilter = areAllGradesChecked() ? grades : showGrades;

    // The new axes are opt-in: all checked OR none checked => filter off (null),
    // which the backend and the table read as "show all". Only a proper subset
    // constrains. The sentinel "Not set" value rides along in the subset.
    const districtsFilter =
      areAllDistrictsChecked() || showDistricts.length === 0 ? null : showDistricts;
    const categoriesFilter =
      areAllCategoriesChecked() || showCategories.length === 0 ? null : showCategories;

    const applied = {
      ...filters,
      states: statesFilter,
      areas: areasFilter,
      sites: sitesFilter,
      years: yearsFilter,
      grades: gradesFilter,
      districts: districtsFilter,
      categories: categoriesFilter,
    };

    setFilters(applied);
    queryPieCharts(applied);
    setIsOpen(false);
  };

  return (
    <Modal show={isOpen} onHide={() => setIsOpen(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Filter</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="filter.state">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>States</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All States"
              checked={areAllStatesChecked()}
              onChange={() => toggleAll(areAllStatesChecked(), states, setShowStates)}
            />
          </div>
          <div className={styles.checkboxes}>
            {states.map(state => (
              <Form.Check
                type="checkbox"
                label={state}
                className={styles['form-check']}
                key={state}
                checked={showStates.includes(state)}
                onChange={() => toggleValue(state, showStates, setShowStates)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.area">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>Areas</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Areas"
              checked={areAllAreasChecked()}
              onChange={() => toggleAll(areAllAreasChecked(), areas, setShowAreas)}
            />
          </div>
          <div className={styles.checkboxes}>
            {areas.map(area => (
              <Form.Check
                type="checkbox"
                label={area}
                className={styles['form-check']}
                key={area}
                checked={showAreas.includes(area)}
                onChange={() => toggleValue(area, showAreas, setShowAreas)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.site">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>Sites</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Sites"
              checked={areAllSitesChecked()}
              onChange={() => toggleAll(areAllSitesChecked(), sites, setShowSites)}
            />
          </div>
          <div className={styles.checkboxes}>
            {sites.map(site => (
              <Form.Check
                type="checkbox"
                label={site}
                className={styles['form-check']}
                key={site}
                checked={showSites.includes(site)}
                onChange={() => toggleValue(site, showSites, setShowSites)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.category">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>Program Type</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Program Types"
              disabled={!siteMapReady}
              checked={areAllCategoriesChecked()}
              onChange={() =>
                toggleAll(areAllCategoriesChecked(), ALL_CATEGORY_VALUES, setShowCategories)
              }
            />
          </div>
          {/* Both site-metadata axes stay disabled until the /sites map loads:
              without it every row misses the lookup and would filter as "Not
              set", disagreeing with the backend-filtered pie chart. */}
          {!siteMapReady && (
            <Form.Text>
              Loading site data. Program Type and School District are unavailable.
            </Form.Text>
          )}
          <div className={styles.checkboxes}>
            {CATEGORY_OPTIONS.map(option => (
              <Form.Check
                type="checkbox"
                label={option.label}
                className={styles['form-check']}
                key={option.value}
                disabled={!siteMapReady}
                checked={showCategories.includes(option.value)}
                onChange={() => toggleValue(option.value, showCategories, setShowCategories)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.district">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>School District</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Districts"
              disabled={!siteMapReady}
              checked={areAllDistrictsChecked()}
              onChange={() => toggleAll(areAllDistrictsChecked(), districts, setShowDistricts)}
            />
          </div>
          <div className={styles.checkboxes}>
            {districts.map(district => (
              <Form.Check
                type="checkbox"
                label={districtLabel(district)}
                className={styles['form-check']}
                key={district}
                disabled={!siteMapReady}
                checked={showDistricts.includes(district)}
                onChange={() => toggleValue(district, showDistricts, setShowDistricts)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.year">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>Enrolled Years</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Years"
              checked={areAllYearsChecked()}
              onChange={() => toggleAll(areAllYearsChecked(), years, setShowYears)}
            />
          </div>
          <div className={styles.checkboxes}>
            {years.map(year => (
              <Form.Check
                type="checkbox"
                label={year}
                className={styles['form-check']}
                key={year}
                checked={showYears.includes(year)}
                onChange={() => toggleValue(year, showYears, setShowYears)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.grade">
          <div className={styles['label-row']}>
            <Form.Label>
              <b>Grade Level</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Grades"
              checked={areAllGradesChecked()}
              onChange={() => toggleAll(areAllGradesChecked(), grades, setShowGrades)}
            />
          </div>
          <div className={styles.checkboxes}>
            {grades.map(grade => (
              <Form.Check
                type="checkbox"
                label={grade}
                className={styles['form-check']}
                key={grade}
                checked={showGrades.includes(grade)}
                onChange={() => toggleValue(grade, showGrades, setShowGrades)}
              />
            ))}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className={styles['button-row']}>
        <Button onClick={resetFilters}>Reset Filters</Button>
        <Button variant="success" onClick={applyFilters}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

AdminStudentFilter.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  states: PropTypes.arrayOf(PropTypes.string).isRequired,
  areas: PropTypes.arrayOf(PropTypes.string).isRequired,
  sites: PropTypes.arrayOf(PropTypes.string).isRequired,
  years: PropTypes.arrayOf(PropTypes.string).isRequired,
  grades: PropTypes.arrayOf(PropTypes.string).isRequired,
  districts: PropTypes.arrayOf(PropTypes.string).isRequired,
  siteMapReady: PropTypes.bool.isRequired,
  filters: PropTypes.oneOfType([PropTypes.object]).isRequired,
  setFilters: PropTypes.func.isRequired,
  queryPieCharts: PropTypes.func.isRequired,
};

export default AdminStudentFilter;
